/** Tight bounding box around a freehand stroke's points. */
export function strokeBBox(stroke) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const [x, y] of stroke.pts) {
        if (x < x0)
            x0 = x;
        if (y < y0)
            y0 = y;
        if (x > x1)
            x1 = x;
        if (y > y1)
            y1 = y;
    }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
/** Bounding box of everything on the board, or null for an empty board. */
export function contentBBox(doc) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, any = false;
    for (const it of doc.items) {
        any = true;
        x0 = Math.min(x0, it.x);
        y0 = Math.min(y0, it.y);
        x1 = Math.max(x1, it.x + it.w);
        y1 = Math.max(y1, it.y + it.h);
    }
    for (const stroke of doc.strokes) {
        any = true;
        const b = strokeBBox(stroke);
        x0 = Math.min(x0, b.x);
        y0 = Math.min(y0, b.y);
        x1 = Math.max(x1, b.x + b.w);
        y1 = Math.max(y1, b.y + b.h);
    }
    return any ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}
/** Do two rectangles share any area? Used for marquee hit-testing. */
export const rectsOverlap = (a, b) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
/** Is `inner` wholly inside `outer`? Used to decide when to re-cull. */
export const rectContains = (outer, inner) => inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h;
export const rectContainsCenter = (outer, inner) => {
    const cx = inner.x + inner.w / 2;
    const cy = inner.y + inner.h / 2;
    return cx >= outer.x && cx <= outer.x + outer.w && cy >= outer.y && cy <= outer.y + outer.h;
};
/**
 * Resolve a connector endpoint to a rectangle.
 *
 * An endpoint may name an item directly, or a child that has since been folded
 * into a group — in which case the connector attaches to the group itself.
 */
export function rectOf(id, items) {
    for (const it of items)
        if (it.id === id)
            return it;
    for (const it of items) {
        if (it.type === 'group' && it.children.some((c) => c.id === id))
            return it;
    }
    return null;
}
/** Union bounding box of the given item ids. */
export function bboxOfItems(ids, items) {
    const wanted = new Set(ids);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity, any = false;
    for (const it of items) {
        if (!wanted.has(it.id))
            continue;
        any = true;
        x0 = Math.min(x0, it.x);
        y0 = Math.min(y0, it.y);
        x1 = Math.max(x1, it.x + it.w);
        y1 = Math.max(y1, it.y + it.h);
    }
    return any ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}
const r1 = (n) => Math.round(n * 10) / 10;
/**
 * Smooth an SVG path through a point list using quadratic segments whose
 * control points are the samples themselves and whose endpoints are the
 * midpoints between samples. Cheap, and it removes the polyline faceting you
 * get from raw pointer samples.
 */
export function pathFromPoints(pts) {
    if (!pts || !pts.length)
        return '';
    let d = `M${r1(pts[0][0])} ${r1(pts[0][1])}`;
    if (pts.length < 3) {
        if (pts[1])
            d += ` L${r1(pts[1][0])} ${r1(pts[1][1])}`;
        return d;
    }
    for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i][0] + pts[i + 1][0]) / 2;
        const my = (pts[i][1] + pts[i + 1][1]) / 2;
        d += ` Q${r1(pts[i][0])} ${r1(pts[i][1])} ${r1(mx)} ${r1(my)}`;
    }
    const last = pts[pts.length - 1];
    return d + ` L${r1(last[0])} ${r1(last[1])}`;
}
/**
 * Route a connector between two rectangles.
 *
 * The dominant axis decides which pair of edges to leave from, so links read as
 * horizontal or vertical flows rather than diagonals. Control points scale with
 * the gap, which keeps short hops from looping and long hops from going slack.
 * The line stops short of the target so the arrowhead sits flush against it.
 */
export function connectorGeometry(a, b) {
    const cax = a.x + a.w / 2, cay = a.y + a.h / 2;
    const cbx = b.x + b.w / 2, cby = b.y + b.h / 2;
    const dx = cbx - cax, dy = cby - cay;
    let p1, p2, unit, c1, c2;
    if (Math.abs(dx) >= Math.abs(dy)) {
        const sx = dx > 0 ? 1 : -1;
        p1 = [dx > 0 ? a.x + a.w : a.x, cay];
        p2 = [dx > 0 ? b.x : b.x + b.w, cby];
        unit = [sx, 0];
        const handle = Math.max(46, Math.abs(p2[0] - p1[0]) * 0.45);
        c1 = [p1[0] + sx * handle, p1[1]];
        c2 = [p2[0] - sx * handle, p2[1]];
    }
    else {
        const sy = dy > 0 ? 1 : -1;
        p1 = [cax, dy > 0 ? a.y + a.h : a.y];
        p2 = [cbx, dy > 0 ? b.y : b.y + b.h];
        unit = [0, sy];
        const handle = Math.max(46, Math.abs(p2[1] - p1[1]) * 0.45);
        c1 = [p1[0], p1[1] + sy * handle];
        c2 = [p2[0], p2[1] - sy * handle];
    }
    const end = [p2[0] - unit[0] * 7, p2[1] - unit[1] * 7];
    const back = [p2[0] - unit[0] * 11, p2[1] - unit[1] * 11];
    const perp = [-unit[1], unit[0]];
    return {
        path: `M${r1(p1[0])} ${r1(p1[1])} C${r1(c1[0])} ${r1(c1[1])} ${r1(c2[0])} ${r1(c2[1])} ${r1(end[0])} ${r1(end[1])}`,
        arrow: `M${r1(p2[0])} ${r1(p2[1])} ` +
            `L${r1(back[0] + perp[0] * 5.5)} ${r1(back[1] + perp[1] * 5.5)} ` +
            `L${r1(back[0] - perp[0] * 5.5)} ${r1(back[1] - perp[1] * 5.5)} Z`,
    };
}
/** Convert a hex color plus alpha into an `rgba()` string. */
export function hexA(hex, alpha) {
    const n = parseInt(hex.slice(1), 16);
    return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}
/** URL-safe slug used for public board links. */
export function slugify(input, max = 48) {
    const base = input
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, max)
        .replace(/-+$/, '');
    return base || 'board';
}
//# sourceMappingURL=geometry.js.map